import { useQuery } from "@tanstack/react-query";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { get, RENTAL_OPTIONS, AMENITY_OPTIONS, TYPE_OPTIONS } from "@/lib/api";

const selectCls = "w-full rounded-xl border border-[#CBD5E1] bg-white px-3 py-2.5 text-sm text-[#0F172A] focus:border-[#03A896]";

const Field = ({ id, label, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-[#334155]">{label}</Label>
    {children}
  </div>
);

export const FiltersPanel = ({ params, set, reset }) => {
  const { data: states = [] } = useQuery({ queryKey: ["states"], queryFn: () => get("/states") });
  const min = Number(params.get("min_fee") || 0);
  const max = Number(params.get("max_fee") || 1500);
  const amen = (params.get("amenities") || "").split(",").filter(Boolean);
  const toggleAmen = (a) => set({ amenities: (amen.includes(a) ? amen.filter((x) => x !== a) : [...amen, a]).join(",") });

  return (
    <div className="space-y-6" data-testid="filters-panel">
      <Field id="filter-state" label="State">
        <select id="filter-state" className={selectCls} value={params.get("state") || ""} onChange={(e) => set({ state: e.target.value, city: "" })} data-testid="filter-state">
          <option value="">All states</option>
          {states.map((s) => <option key={s.slug} value={s.slug}>{s.state} ({s.count})</option>)}
        </select>
      </Field>
      <Field id="filter-fee" label={`Monthly fee: $${min} - $${max}${max >= 1500 ? "+" : ""}`}>
        <Slider id="filter-fee" min={0} max={1500} step={25} defaultValue={[min, max]} key={`${min}-${max}`} data-testid="filter-fee-range" className="py-3"
          aria-label="Monthly fee range" onValueCommit={([a, b]) => setTimeout(() => set({ min_fee: a > 0 ? a : "", max_fee: b < 1500 ? b : "" }))} />
      </Field>
      <Field id="filter-pets" label="Pet policy">
        <select id="filter-pets" className={selectCls} value={params.get("pets") || ""} onChange={(e) => set({ pets: e.target.value })} data-testid="filter-pet-policy">
          <option value="">Any</option><option value="yes">Pets allowed</option><option value="no">No pets</option>
        </select>
      </Field>
      <Field id="filter-rental" label="Rental restrictions">
        <select id="filter-rental" className={selectCls} value={params.get("rental") || ""} onChange={(e) => set({ rental: e.target.value })} data-testid="filter-rental-restrictions">
          <option value="">Any</option>
          {RENTAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field id="filter-type" label="Community type">
        <select id="filter-type" className={selectCls} value={params.get("type") || ""} onChange={(e) => set({ type: e.target.value })} data-testid="filter-community-type">
          <option value="">Any</option>
          {TYPE_OPTIONS.map((t) => <option key={t}>{t}</option>)}
        </select>
      </Field>
      <fieldset className="space-y-2">
        <legend className="text-xs font-semibold uppercase tracking-wider text-[#334155] mb-2">Amenities</legend>
        {AMENITY_OPTIONS.map((a) => (
          <label key={a} className="flex items-center gap-2 text-sm text-[#334155] cursor-pointer">
            <Checkbox checked={amen.includes(a)} onCheckedChange={() => toggleAmen(a)} data-testid={`filter-amenity-${a.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
            {a}
          </label>
        ))}
      </fieldset>
      <button onClick={reset} className="w-full rounded-full border border-[#0B2150] px-4 py-2 text-sm font-semibold text-[#0B2150] hover:bg-[#0B2150] hover:text-white transition-colors" data-testid="filters-reset-btn">
        Reset filters
      </button>
    </div>
  );
};
